import _ from 'lodash';
import React, { ReactNode } from 'react';
import { generateUUID, getUniqueKey } from '../_utils';
import { IconID, IconProps } from '../icon';
import { Menu, MenuTheme } from '../menu';
import { MenuItemEventHandler } from '../menu/menu-item';

export interface ActionMenuItem {
  /**
   * MenuItem Id
   */
  id: string;
  /**
   * MenuItem Label
   */
  label?: string;
  /**
   * MenuItem Icon to be shown on the left
   */
  icon?: IconID;
  /**
   * MenuItem custom Icon to be shown on the left
   */
  customIcon?: IconProps;
  /**
   * flag to disable MenuItem
   */
  itemDisabled?: boolean;
  /**
   * Tooltip when MenuItem is disabled
   */
  itemDisabledTooltip?: string;
  /**
   * Tooltip when MenuItem is not disabled
   */
  itemTooltip?: string;
  /**
   * Custom component to be rendered in the menuItem
   */
  customContent?: ReactNode;
  /**
   * Click Handler on MenuItem
   */
  onClick?: ($event?: MenuItemEventHandler) => void;
  /**
   * Cascading MenuItem with Group list to be shown
   */
  cascadingItems?: ActionMenuItemGroup[];
  /**
   * TODO need to go through the implementation
   */
  isSelected?: boolean;
  /**
   * Custom class name
   */
  className?: string;

  /**
   * Add "track-id" attribute for user click event in auto-track-service
   */
  trackId?: string;
  /**
   * href for RoutedLink Component
   */
  href?: string;
  /**
   * Generic container for data
   */
  data?: any;
}

export interface ActionMenuItemGroup {
  title: string;
  items: ActionMenuItem[];
}

export interface ActionMenuProps {
  /** list of menuItems to be renderd */
  menuItemGroups: ActionMenuItemGroup[];

  /** id for actionMenu */
  id?: string;

  /** flag to show ActionMenu
   * @default false
   */
  show?: boolean;

  /**
   * Custom class name
   */
  className?: string;

  /**
   * Don't handle tab navigation if already handled by component
   */
  disableKeyboardNavigation?: boolean;

  /**
   * Prop to set the theme for the menu
   */
  menuTheme?: MenuTheme;

  /**
   * Disable tab index for the menu container to prevent double tab stops
   * @default false
   */
  disableMenuTabIndex?: boolean;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  menuItemGroups,
  id: Id,
  show = false,
  className = '',
  disableKeyboardNavigation = false,
  menuTheme = undefined,
  disableMenuTabIndex = false,
  ...restProps
}: ActionMenuProps) => {
  const renderChildren = (menuItem: ActionMenuItem) => {
    const { cascadingItems, customContent } = menuItem;
    if (cascadingItems) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return renderCascadingItems(menuItem);
    }
    return customContent;
  };

  const checkHasLeftIcon = (menuItemGroup: ActionMenuItemGroup[] | undefined) => {
    return (
      menuItemGroup &&
      menuItemGroup.some((group: ActionMenuItemGroup) =>
        group.items.some((item) => item.icon || item.isSelected)
      )
    );
  };

  const checkHasRightIcon = (menuItemGroup: ActionMenuItemGroup[] | undefined) => {
    return (
      menuItemGroup &&
      menuItemGroup.some((group: ActionMenuItemGroup) =>
        group.items.some((item) => item.cascadingItems)
      )
    );
  };

  const renderMenuItem = (
    menuItem: ActionMenuItem,
    hasChild = false,
    hasLeftIcon = false,
    hasRightIcon = false
  ) => {
    const {
      id,
      label,
      icon,
      customIcon,
      itemDisabled,
      onClick,
      isSelected,
      itemTooltip,
      itemDisabledTooltip,
      trackId,
      ...rest
    } = menuItem;

    let defaultIcon = isSelected ? IconID.CHECKMARK : icon;
    if (customIcon) {
      defaultIcon = customIcon.id;
    }
    if (!defaultIcon && hasLeftIcon) {
      defaultIcon = IconID.EMPTY;
    }
    return (
      <Menu.Item
        key={generateUUID()}
        label={label!}
        id={id}
        iconName={defaultIcon}
        customIcon={customIcon}
        disabled={itemDisabled as boolean}
        onSelect={onClick}
        hasRightIconInGroup={hasRightIcon}
        itemTooltip={itemTooltip}
        itemDisabledTooltip={itemDisabledTooltip}
        trackId={trackId}
        // omit props which are not relevant to Menu.Item
        {..._.omit(rest, 'cascadingItems', 'customContent')}
      >
        {hasChild && renderChildren(menuItem)}
      </Menu.Item>
    );
  };

  const renderGroup = (
    groupMenuItem: ActionMenuItemGroup,
    showSeparator = false,
    hasLeftIcon = false,
    hasRightIcon = false
  ) => (
    <Menu.Group key={generateUUID()} showSeparator={showSeparator}>
      <>
        {groupMenuItem.items.map((groupItem: ActionMenuItem) => {
          const { cascadingItems, customContent } = groupItem as ActionMenuItem;
          if (cascadingItems || customContent) {
            return renderMenuItem(groupItem, true, hasLeftIcon, hasRightIcon);
          }
          return renderMenuItem(groupItem, false, hasLeftIcon, hasRightIcon);
        })}
      </>
    </Menu.Group>
  );

  const renderCascadingItems = (menuItem: ActionMenuItem) => {
    const { id, label, cascadingItems } = menuItem;
    const hasLeftIcon = checkHasLeftIcon(cascadingItems);
    const hasRightIcon = checkHasRightIcon(cascadingItems);
    return (
      !_.isEmpty(cascadingItems) && (
        <Menu id={`cascading_menu${id}${label}`} show={show}>
          {cascadingItems!.map(
            (cascadingItem: ActionMenuItem | ActionMenuItemGroup, index: number) => {
              if ('items' in cascadingItem) {
                return renderGroup(
                  cascadingItem as ActionMenuItemGroup,
                  !(cascadingItems!.length - 1 === index),
                  hasLeftIcon,
                  hasRightIcon
                );
              }
              return renderMenuItem(cascadingItem, false, hasLeftIcon, hasRightIcon);
            }
          )}
        </Menu>
      )
    );
  };

  const hasLeftIcon = checkHasLeftIcon(menuItemGroups);
  const hasRighttIcon = checkHasRightIcon(menuItemGroups);
  return (
    <>
      {show && !_.isEmpty(menuItemGroups) && (
        <Menu
          id={`actionmenu_${Id || getUniqueKey('default')}`}
          show
          className={className}
          disableKeyboardNavigation={disableKeyboardNavigation}
          menuTheme={menuTheme}
          disableTabIndex={disableMenuTabIndex}
          {...restProps}
        >
          {menuItemGroups.map((menuItem: ActionMenuItemGroup, index: number) =>
            renderGroup(
              menuItem as ActionMenuItemGroup,
              !(menuItemGroups.length - 1 === index),
              hasLeftIcon,
              hasRighttIcon
            )
          )}
        </Menu>
      )}
    </>
  );
};
